import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle2, Clock, ShieldCheck, UserX, RotateCcw, XCircle, Mail, Landmark, MapPin, Phone, ChevronDown, ChevronUp, Search, ArrowRight, FileDown, Archive, ArchiveRestore } from "lucide-react";
import { api } from "../../lib/api";
import StaffDetailDrawer from "./StaffDetailDrawer";

/**
 * Founder-only "HR" console. Shows every admin user with their onboarding
 * lifecycle, activity snapshot (leads/bookings/clients they own), and lets
 * founders approve, deny, finalize onboarding, or terminate a hire.
 *
 * Silently renders nothing if the caller isn't a founder (403 from /staff).
 */

const LIFECYCLE_META = {
  founder:               { label: "Founder",              tone: "gold" },
  active:                { label: "Active",               tone: "green" },
  needs_onboarding:      { label: "Needs onboarding",     tone: "amber" },
  awaiting_finalization: { label: "Awaiting finalization", tone: "gold" },
  awaiting_approval:     { label: "Awaiting approval",    tone: "amber" },
  denied:                { label: "Denied",               tone: "red" },
  terminated:            { label: "Terminated",           tone: "red" },
};

const TONE_CLASSES = {
  gold:  "bg-[#c9a961]/15 text-[#a3823f] border-[#c9a961]/40",
  green: "bg-[#6b7c3a]/12 text-[#4d5a24] border-[#6b7c3a]/40",
  amber: "bg-[#c9a961]/22 text-[#a3823f] border-[#c9a961]/50",
  red:   "bg-[#b23a48]/10 text-[#b23a48] border-[#b23a48]/40",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

export default function StaffAdmin() {
  const [rows, setRows] = useState(null); // null=loading, "denied"=non-founder, []=loaded
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState({});
  const [q, setQ] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);
  const [drawerUserId, setDrawerUserId] = useState(null); // opens StaffDetailDrawer

  const load = async () => {
    try {
      const { data } = await api.get(`/admin-auth/staff${showArchived ? "?include_archived=1" : ""}`);
      setRows(data.staff || []);
      setArchivedCount(data.archived_count || 0);
    } catch (e) {
      if (e?.response?.status === 403) setRows("denied");
      else toast.error("Could not load staff roster.");
    }
  };
  useEffect(() => { load(); }, [showArchived]); // eslint-disable-line

  const setBusyKey = (k, v) => setBusy((b) => v ? { ...b, [k]: true } : (() => { const { [k]: _, ...rest } = b; return rest; })());

  const downloadPayrollCsv = async () => {
    setBusyKey("__payroll__", true);
    try {
      const { data } = await api.get("/admin-auth/payroll.csv", { responseType: "blob" });
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `elevare-payroll-${today}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Payroll CSV downloaded.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not export payroll.");
    } finally { setBusyKey("__payroll__", false); }
  };

  const approve = async (u) => {
    setBusyKey(u.id, true);
    try {
      await api.patch(`/admin-auth/users/${u.id}/review`, { status: "approved" });
      toast.success(`${u.display_name || u.username} approved.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not approve."); }
    finally { setBusyKey(u.id, false); }
  };
  const deny = async (u) => {
    if (!window.confirm(`Deny ${u.display_name || u.username}? They will not be able to log in.`)) return;
    setBusyKey(u.id, true);
    try {
      await api.patch(`/admin-auth/users/${u.id}/review`, { status: "denied" });
      toast.success("Denied.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not deny."); }
    finally { setBusyKey(u.id, false); }
  };
  const finalize = async (u) => {
    setBusyKey(u.id, true);
    try {
      await api.patch(`/admin-auth/onboarding/${u.id}/finalize`);
      toast.success(`${u.display_name || u.username} is now cleared to work.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not finalize."); }
    finally { setBusyKey(u.id, false); }
  };
  const terminate = async (u) => {
    const reason = window.prompt(
      `Terminate ${u.display_name || u.username}?\n\nThis revokes login, blocks their JWT, and emails them a notice.\nOptional: enter the reason (kept in the audit log).`,
      "",
    );
    if (reason === null) return; // cancelled
    setBusyKey(u.id, true);
    try {
      await api.post(`/admin-auth/users/${u.id}/terminate`, { reason: reason || undefined });
      toast.success(`${u.display_name || u.username} terminated.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not terminate."); }
    finally { setBusyKey(u.id, false); }
  };
  const reinstate = async (u) => {
    if (!window.confirm(`Reinstate ${u.display_name || u.username}? They'll be able to log back in immediately.`)) return;
    setBusyKey(u.id, true);
    try {
      await api.post(`/admin-auth/users/${u.id}/reinstate`);
      toast.success("Reinstated.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reinstate."); }
    finally { setBusyKey(u.id, false); }
  };

  const archive = async (u) => {
    if (!window.confirm(`Archive ${u.display_name || u.username}?\n\nThe row will be hidden from the active roster. Onboarding paperwork and payroll history stay intact for compliance. You can restore them later with "Show archived".`)) return;
    setBusyKey(u.id, true);
    try {
      await api.post(`/admin-auth/users/${u.id}/archive`);
      toast.success(`${u.display_name || u.username} archived.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not archive."); }
    finally { setBusyKey(u.id, false); }
  };

  const unarchive = async (u) => {
    setBusyKey(u.id, true);
    try {
      await api.post(`/admin-auth/users/${u.id}/unarchive`);
      toast.success("Restored to roster.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore."); }
    finally { setBusyKey(u.id, false); }
  };

  const counts = useMemo(() => {
    if (!Array.isArray(rows)) return { all: 0 };
    const c = { all: rows.length };
    for (const r of rows) {
      const k = r.lifecycle;
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (lifecycleFilter !== "all" && r.lifecycle !== lifecycleFilter) return false;
      if (!needle) return true;
      const hay = `${r.display_name || ""} ${r.username || ""} ${r.email || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, lifecycleFilter]);

  if (rows === null) {
    return (
      <div data-testid="staff-loading" className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-[#c9a961]" />
      </div>
    );
  }
  if (rows === "denied") {
    return (
      <div data-testid="staff-denied" className="rounded-2xl border border-[#b23a48]/30 bg-[#b23a48]/8 p-6 text-center">
        <ShieldCheck className="h-6 w-6 mx-auto text-[#b23a48]" />
        <p className="mt-2 text-sm text-[#b23a48]">Founder access required. Only Kalib and John can manage staff.</p>
      </div>
    );
  }

  return (
    <div data-testid="tab-content-staff" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#a3823f]" />
          <span className="text-[11px] uppercase tracking-[0.24em] text-[#1e3d3a]/60">Staff roster</span>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/45">· {counts.all} total</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="staff-toggle-archived"
            onClick={() => setShowArchived((s) => !s)}
            title={showArchived ? "Hide archived staff" : "Show archived staff too"}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
              showArchived
                ? "bg-[#1e3d3a] text-white"
                : "border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961]"
            }`}
          >
            <Archive className="h-3 w-3" />
            {showArchived ? "Hiding" : "Show"} archived
            {archivedCount > 0 && (
              <span className={`tabular-nums text-[9px] rounded-full px-1.5 ${showArchived ? "bg-[#c9a961] text-[#1e3d3a]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"}`}>
                {archivedCount}
              </span>
            )}
          </button>
          <button
            type="button"
            data-testid="staff-payroll-export"
            onClick={downloadPayrollCsv}
            disabled={busy["__payroll__"]}
            title="Download payroll CSV — active hires only"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#a3823f] text-white px-3 py-2 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
          >
            {busy["__payroll__"] ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
            Payroll CSV
          </button>
          <div className="relative">
            <Search className="h-4 w-4 text-[#1e3d3a]/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="staff-search"
              type="text"
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search by name, username, or email"
              className="rounded-full border border-[#1e3d3a]/12 bg-white pl-9 pr-4 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25 min-w-[260px]"
            />
          </div>
        </div>
      </div>

      {/* Lifecycle filter chips */}
      <div className="flex gap-1.5 flex-wrap" data-testid="staff-filter-chips">
        {[
          { id: "all", label: "All" },
          { id: "active", label: "Active" },
          { id: "needs_onboarding", label: "Needs onboarding" },
          { id: "awaiting_finalization", label: "Awaiting finalization" },
          { id: "awaiting_approval", label: "Awaiting approval" },
          { id: "terminated", label: "Terminated" },
          { id: "founder", label: "Founder" },
        ].map((c) => {
          const active = lifecycleFilter === c.id;
          const n = c.id === "all" ? counts.all : (counts[c.id] || 0);
          return (
            <button
              key={c.id}
              type="button"
              data-testid={`staff-filter-${c.id}`}
              onClick={() => setLifecycleFilter(c.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                active ? "bg-[#1e3d3a] text-white" : "border border-[#1e3d3a]/15 text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#c9a961]"
              }`}
            >
              {c.label}
              <span className={`tabular-nums text-[9px] rounded-full px-1.5 ${active ? "bg-[#c9a961] text-[#1e3d3a]" : "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#eae7de] text-[10px] uppercase tracking-[0.25em] text-[#1e3d3a]/60">
            <tr>
              <th className="text-left px-4 py-3 w-8"></th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-right px-4 py-3">Leads</th>
              <th className="text-right px-4 py-3">Bookings</th>
              <th className="text-right px-4 py-3">Clients</th>
              <th className="text-left px-4 py-3">Joined</th>
              <th className="text-right px-4 py-3 w-52">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="8" className="text-center text-[#1e3d3a]/40 py-8">No staff match this filter.</td></tr>
            )}
            {filtered.map((r) => {
              const meta = LIFECYCLE_META[r.lifecycle] || { label: r.lifecycle, tone: "gold" };
              const isOpen = expanded === r.id;
              const isBusy = !!busy[r.id];
              return (
                <>
                  <tr
                    key={r.id}
                    data-testid={`staff-row-${r.username}`}
                    className="border-t border-[#1e3d3a]/8 hover:bg-[#c9a961]/4 transition-colors duration-200"
                  >
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        data-testid={`staff-toggle-${r.username}`}
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                        className="text-[#1e3d3a]/50 hover:text-[#1e3d3a]"
                      >
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        data-testid={`staff-open-${r.username}`}
                        onClick={() => setDrawerUserId(r.id)}
                        className="text-left group"
                      >
                        <div className="font-medium text-[#1e3d3a] group-hover:text-[#a3823f] transition-colors duration-200 inline-flex items-center gap-1">
                          {r.display_name || r.username}
                          <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">@{r.username}</div>
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        data-testid={`staff-stage-${r.username}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] ${TONE_CLASSES[meta.tone] || TONE_CLASSES.gold}`}
                      >
                        {meta.label}
                      </span>
                      {r.archived_at && (
                        <span
                          data-testid={`staff-archived-${r.username}`}
                          title={`Archived ${fmtDate(r.archived_at)}${r.archived_by ? ` by ${r.archived_by}` : ""}`}
                          className="ml-1.5 inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 bg-[#eae7de] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/55"
                        >
                          <Archive className="h-2.5 w-2.5" /> Archived
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#1e3d3a]/75">{r.leads_count || 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#1e3d3a]/75">{r.bookings_count || 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#1e3d3a]/75">{r.clients_count || 0}</td>
                    <td className="px-4 py-3 text-[#1e3d3a]/60 text-[11px]">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        {r.lifecycle === "awaiting_approval" && (
                          <>
                            <button type="button" data-testid={`staff-approve-${r.username}`}
                              onClick={() => approve(r)} disabled={isBusy}
                              className="inline-flex items-center gap-1 rounded-full bg-[#6b7c3a] text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#4d5a24] disabled:opacity-60 transition-colors duration-300"
                            >
                              {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Approve
                            </button>
                            <button type="button" data-testid={`staff-deny-${r.username}`}
                              onClick={() => deny(r)} disabled={isBusy}
                              className="inline-flex items-center gap-1 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#b23a48]/10 disabled:opacity-60 transition-colors duration-300"
                            >
                              <XCircle className="h-3 w-3" /> Deny
                            </button>
                          </>
                        )}
                        {r.lifecycle === "awaiting_finalization" && (
                          <button type="button" data-testid={`staff-finalize-${r.username}`}
                            onClick={() => finalize(r)} disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-full bg-[#a3823f] text-white px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />} Finalize
                          </button>
                        )}
                        {(r.lifecycle === "active" || r.lifecycle === "needs_onboarding" || r.lifecycle === "awaiting_finalization") && r.role !== "founder" && (
                          <button type="button" data-testid={`staff-terminate-${r.username}`}
                            onClick={() => terminate(r)} disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-full border border-[#b23a48]/40 text-[#b23a48] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#b23a48]/10 disabled:opacity-60 transition-colors duration-300"
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserX className="h-3 w-3" />} Fire
                          </button>
                        )}
                        {r.lifecycle === "terminated" && (
                          <button type="button" data-testid={`staff-reinstate-${r.username}`}
                            onClick={() => reinstate(r)} disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-full border border-[#6b7c3a]/40 text-[#4d5a24] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#6b7c3a]/10 disabled:opacity-60 transition-colors duration-300"
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Reinstate
                          </button>
                        )}
                        {r.lifecycle === "terminated" && !r.archived_at && (
                          <button type="button" data-testid={`staff-archive-${r.username}`}
                            onClick={() => archive(r)} disabled={isBusy}
                            title="Hide from active roster (history preserved)"
                            className="inline-flex items-center gap-1 rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a]/60 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#1e3d3a]/8 hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />} Archive
                          </button>
                        )}
                        {r.archived_at && (
                          <button type="button" data-testid={`staff-unarchive-${r.username}`}
                            onClick={() => unarchive(r)} disabled={isBusy}
                            title="Restore to active roster (still terminated)"
                            className="inline-flex items-center gap-1 rounded-full border border-[#c9a961]/40 text-[#a3823f] px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-[#c9a961]/10 disabled:opacity-60 transition-colors duration-300"
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArchiveRestore className="h-3 w-3" />} Unarchive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`staff-expand-${r.username}`} className="border-t border-[#1e3d3a]/6 bg-[#eae7de]/40">
                      <td colSpan="8" className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-[12px]">
                          <ExpandCard title="Contact">
                            <Line icon={Mail}  label="Email" val={r.email || "—"} />
                            <Line icon={Phone} label="Phone" val={r.onboarding_profile.phone || "—"} />
                            <Line icon={MapPin} label="Address" val={r.onboarding_profile.address_line1
                              ? `${r.onboarding_profile.address_line1}${r.onboarding_profile.address_line2 ? ", " + r.onboarding_profile.address_line2 : ""}, ${r.onboarding_profile.city}, ${r.onboarding_profile.state} ${r.onboarding_profile.postal_code}` : "—"} />
                          </ExpandCard>
                          <ExpandCard title="Payroll">
                            <Line icon={Landmark} label="Bank" val={r.onboarding_profile.bank_name || "—"} />
                            <Line icon={Landmark} label="Routing" val={r.onboarding_profile.bank_routing || "—"} />
                            <Line icon={Landmark} label="Account" val={r.bank_last4 ? `••••${r.bank_last4}` : "—"} />
                            <Line icon={Landmark} label="Type" val={r.onboarding_profile.bank_account_type || "—"} />
                          </ExpandCard>
                          <ExpandCard title="Lifecycle history">
                            <Line icon={Clock} label="Registered" val={fmtDate(r.created_at)} />
                            <Line icon={CheckCircle2} label="Approved" val={fmtDate(r.approved_at)} />
                            <Line icon={ShieldCheck} label="Onboarding submitted" val={fmtDate(r.onboarding_submitted_at)} />
                            <Line icon={ShieldCheck} label="Finalized" val={r.onboarding_finalized_at ? `${fmtDate(r.onboarding_finalized_at)} by ${r.onboarding_finalized_by || "—"}` : "—"} />
                            {r.terminated_at && (
                              <>
                                <Line icon={UserX} label="Terminated" val={`${fmtDate(r.terminated_at)} by ${r.terminated_by || "—"}`} />
                                {r.termination_reason && <Line icon={UserX} label="Reason" val={r.termination_reason} />}
                              </>
                            )}
                          </ExpandCard>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      {drawerUserId && (
        <StaffDetailDrawer
          userId={drawerUserId}
          onClose={() => setDrawerUserId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function ExpandCard({ title, children }) {
  return (
    <div className="rounded-xl border border-[#1e3d3a]/10 bg-white p-3">
      <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] mb-2">{title}</p>
      <ul className="space-y-1.5">{children}</ul>
    </div>
  );
}
function Line({ icon: Icon, label, val }) {
  return (
    <li className="flex items-baseline gap-2">
      <Icon className="h-3 w-3 text-[#c9a961] shrink-0" />
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">{label}</span>
      <span className="ml-auto text-right text-[12px] text-[#1e3d3a] truncate">{val || "—"}</span>
    </li>
  );
}
